import { Controller } from "@hotwired/stimulus"

// Drag an appointment to reschedule it.
//
// What a drop means depends on where it lands, which is what each dropzone declares in
// data-drop-precision:
//   day     — month grid: move by whole days, keep the time of day (the grid has no time)
//   time    — week/day hour columns: move to the minute under the pointer, snapped
//   all_day — the all-day lane: move by whole days and make the appointment all-day
//
// The server does the arithmetic — see CalendarController#update_appointment_schedule.
//
// In the month grid and the all-day lane the entries are laid over the day cells rather
// than inside them (a multi-day bar spans several), so the dropzone is the cell *under* the
// pointer, not an ancestor of the event target. A bar can be grabbed on any of its days:
// the start moves by however many days the pointer travelled from the grabbed one, so the
// day under the pointer stays the day that was grabbed.
const SNAP_MINUTES = 15
const MINUTES_PER_DAY = 1440
const MS_PER_DAY = 86400000
const DROPZONE = "[data-calendar-drag-target='dropzone']"

export default class extends Controller {
  static targets = ["chip", "dropzone"]
  static values = { url: String, view: String, viewDate: String }

  connect() {
    this.dragging = null
  }

  disconnect() {
    this.clearHighlights()
  }

  start(event) {
    const chip = event.target.closest("[data-calendar-drag-target='chip']")
    if (!chip) return
    this.dragging = chip
    this.grabbedZone = this.zoneAt(event)
    chip.classList.add("opacity-40")

    event.dataTransfer.effectAllowed = "move"
    // Firefox only starts a drag when some data is set.
    event.dataTransfer.setData("text/plain", chip.dataset.appointmentId || "")
  }

  end() {
    if (this.dragging) this.dragging.classList.remove("opacity-40")
    this.clearHighlights()
    this.dragging = null
    this.grabbedZone = null
  }

  over(event) {
    if (!this.dragging) return
    const zone = this.zoneAt(event)
    if (!zone) {
      this.clearHighlights()
      return
    }

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (zone !== this.highlighted) {
      this.clearHighlights()
      zone.classList.add("ring-2", "ring-inset", "ring-green-400")
      this.highlighted = zone
    }

    // In a time grid the day alone says nothing — show where in the day it would land.
    if (zone.dataset.dropPrecision === "time") this.showTimeIndicator(zone, event.clientY)
  }

  // The left element is often a bar lying over the cells, not a cell, so the target says
  // nothing about which cell was left. Moving anywhere inside the calendar is left to the
  // next dragover, which finds the cell beneath the pointer (or none) and repaints; only
  // leaving the calendar itself clears here. Some browsers give no relatedTarget at all —
  // that clears too, and the next dragover paints the cell again.
  leave(event) {
    if (!this.highlighted) return
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.clearHighlights()
  }

  async drop(event) {
    if (!this.dragging) return
    const zone = this.zoneAt(event)
    if (!zone) return

    event.preventDefault()
    const appointmentId = this.dragging.dataset.appointmentId
    const precision = zone.dataset.dropPrecision || "day"
    const date = this.targetDate(this.dragging, this.grabbedZone, zone, precision)
    const minutes = precision === "time" ? this.minutesAt(zone, event.clientY) : null
    const origin = this.grabbedZone
    this.end()

    if (!appointmentId || !date) return
    // Dropped back on the cell it came from. The server ignores an unchanged move anyway;
    // this just saves the round-trip and the full grid re-render.
    if (precision === "day" && origin === zone) return

    const body = new FormData()
    body.append("date", date)
    body.append("precision", precision)
    if (minutes !== null) body.append("start_minutes", minutes)
    // The window to re-render is the one on screen, not the day dropped on: dropping onto a
    // trailing day of the grid would otherwise page the calendar to the next month.
    body.append("view", this.viewValue)
    body.append("view_date", this.viewDateValue)
    body.append("_method", "patch")

    const response = await fetch(this.urlValue.replace("__ID__", appointmentId), {
      method: "POST",
      body,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
      },
      credentials: "same-origin",
    })

    const html = await response.text()
    if (html) window.Turbo.renderStreamMessage(html)
  }

  // The dropzone at the pointer: an ancestor of the target when the entry sits inside its
  // day (the time grid), else the cell lying beneath the bar or footer the pointer is over.
  zoneAt(event) {
    const own = event.target.closest?.(DROPZONE)
    if (own) return own
    if (typeof document.elementsFromPoint !== "function") return null

    return (
      document
        .elementsFromPoint(event.clientX, event.clientY)
        .find((element) => element.matches(DROPZONE) && this.element.contains(element)) || null
    )
  }

  // The date the server should move the start to. A day-precision drop of an entry that
  // knows its start keeps the grabbed day under the pointer; everything else lands on the
  // day it was dropped on, as before.
  targetDate(chip, grabbedZone, zone, precision) {
    const dropped = zone.dataset.date
    const start = chip.dataset.startDate
    const grabbed = grabbedZone?.dataset.date
    if (precision === "time" || !dropped || !start || !grabbed) return dropped

    return this.shiftDate(start, this.daysBetween(grabbed, dropped))
  }

  // ISO dates read as UTC midnight, so a DST change in between cannot cost an hour and
  // with it a day.
  daysBetween(from, to) {
    return Math.round(
      (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY,
    )
  }

  shiftDate(iso, days) {
    return new Date(Date.parse(`${iso}T00:00:00Z`) + days * MS_PER_DAY).toISOString().slice(0, 10)
  }

  // The column spans a full 24 hours, so the pointer's offset into it is the time of day.
  minutesAt(zone, clientY) {
    const rect = zone.getBoundingClientRect()
    if (!rect.height) return 0

    const raw = ((clientY - rect.top) / rect.height) * MINUTES_PER_DAY
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
    return Math.min(Math.max(snapped, 0), MINUTES_PER_DAY - SNAP_MINUTES)
  }

  showTimeIndicator(zone, clientY) {
    const minutes = this.minutesAt(zone, clientY)
    const indicator = this.timeIndicator()

    indicator.style.top = `${(minutes / MINUTES_PER_DAY) * 100}%`
    indicator.firstElementChild.textContent = this.formatMinutes(minutes)
    if (indicator.parentElement !== zone) zone.appendChild(indicator)
  }

  timeIndicator() {
    if (this.indicator) return this.indicator

    const indicator = document.createElement("div")
    indicator.className =
      "pointer-events-none absolute inset-x-0 z-30 flex items-center border-t-2 border-green-500"
    const label = document.createElement("span")
    label.className = "rounded bg-green-600 px-1 text-[10px] font-medium text-white tabular-nums"
    indicator.appendChild(label)

    this.indicator = indicator
    return indicator
  }

  formatMinutes(minutes) {
    const hours = String(Math.floor(minutes / 60)).padStart(2, "0")
    return `${hours}:${String(minutes % 60).padStart(2, "0")}`
  }

  clearHighlights() {
    if (this.highlighted) {
      this.highlighted.classList.remove("ring-2", "ring-inset", "ring-green-400")
      this.highlighted = null
    }
    if (this.indicator) this.indicator.remove()
  }
}
